import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

import { MagnifyingGlassIcon } from "@heroicons/vue/24/outline";
import * as Highcharts from "highcharts";
import merge from "lodash.merge";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import LoadingStateEmitter from "shared/components/loading_state/LoadingStateEmitter.vue";
import { useHierarchyTags } from "shared/composables/hierarchyTags";
import { useCriticalPath, usePlanConfig } from "shared/composables/planner";
import { useProjectDurationSettings } from "shared/composables/projectDurationSettings";
import { useTrackEvent } from "shared/composables/tracking";
import { useWatchKey } from "shared/composables/vue";
import CriticalPathDelay from "shared/views/critical_path/components/CriticalPathDelay.vue";
import CriticalPathModal from "shared/views/critical_path/components/CriticalPathModal.vue";
import { getCriticalPathContextOrNull } from "shared/views/critical_path/criticalPath";
import { createCriticalPathExOrNull } from "shared/views/critical_path/criticalPathNode";
import { DashboardGridSize } from "@/types/Dashboard";
import { MilestoneReportConfig, MilestoneReportFilters } from "@/types/reports/PlotMilestone";
import ChartOrNoData from "@/views/reports/components/ChartOrNoData.vue";
import { useChartSize } from "@/views/reports/composables";
import ReportFilterBadges from "@/views/reports/filters/ReportFilterBadges.vue";
import MilestonePlotDeltaLabels from "@/views/reports/plots/milestone/MilestonePlotDeltaLabels.vue";
import { getSeriesChartConfig } from "@/views/reports/plots/milestone/milestoneChartOptions";
import {
  getEntireProjectGroup,
  getCriticalPathGroups,
} from "@/views/reports/plots/milestone/milestoneCriticalPath";
import { MilestonePlotGroup } from "@/views/reports/plots/milestone/types";


export default /*#__PURE__*/_defineComponent({
  __name: 'MilestonePlotDefault',
  props: {
    reportId: {},
    filters: {},
    config: {},
    title: {},
    width: {},
    height: {},
    spacingX: {},
    spacingY: {},
    dashboardGridSize: {}
  },
  emits: ["widgetRouteClicked"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

const props = __props;

const emit = __emit;

const { planConfig, isLoading: isPlanConfigLoading } = usePlanConfig();
const { hierarchyTags, isLoading: areHierarchyTagsLoading } = useHierarchyTags();
const { criticalPath, isLoading: isCriticalPathLoading } = useCriticalPath();
const { projectDurationSettings, isProjectDurationSettingsLoading } = useProjectDurationSettings();

const trackEvent = useTrackEvent();

const { headerHeight, chartHeight, sizeStyles, filterBadgesHeight } = useChartSize(
  computed(() => ({ ...props })),
  {
    minGridColumns: 2,
    minGridRows: 2,
  },
);

const isCriticalPathModalOpen = ref(false);
const isChartZoomed = ref(false);

const isLoading = computed(
  () =>
    isPlanConfigLoading.value ||
    areHierarchyTagsLoading.value ||
    isCriticalPathLoading.value ||
    isProjectDurationSettingsLoading.value,
);

const criticalPathEx = computed(() =>
  createCriticalPathExOrNull(criticalPath.value, planConfig.value, hierarchyTags.value),
);

const context = computed(() =>
  getCriticalPathContextOrNull(
    criticalPathEx.value,
    planConfig.value,
    hierarchyTags.value,
    projectDurationSettings.value,
  ),
);

const criticalPathGroups = computed<MilestonePlotGroup[] | null>(() => {
  if (!context.value || !criticalPathEx.value) {
    return null;
  }
  return props.config.mode === "end_date"
    ? getEntireProjectGroup(criticalPathEx.value, context.value, props.filters.location)
    : getCriticalPathGroups(criticalPathEx.value, context.value, props.filters.location);
});

const chartOptions = computed(() => {
  if (!context.value || !criticalPathGroups.value) {
    return null;
  }
  const seriesChartConfig = getSeriesChartConfig(
    criticalPathGroups.value,
    context.value,
    chartHeight.value,
    props.config,
  );
  const zoomOptions: Highcharts.Options = {
    chart: {
      events: {
        selection: (event: Highcharts.SelectEventObject) => {
          isChartZoomed.value = !!event.yAxis;
          return undefined;
        },
      },
    },
  };
  return merge(seriesChartConfig, zoomOptions);
});

// When the chart options change, highchart "sorts" the x values and the graph is broken.
// We just recreate the whole graph when the options change.
const chartKey = useWatchKey(chartOptions);

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createElementBlock("div", {
    class: "relative",
    style: _normalizeStyle(_unref(sizeStyles))
  }, [
    _createElementVNode("div", {
      class: "text-center pl-6 pr-8 truncate",
      style: _normalizeStyle({ height: `${_unref(headerHeight)}px` })
    }, [
      _createVNode(_component_RouterLink, {
        to: {
          name: 'Reports',
          query: {
            report: _ctx.reportId,
          },
        },
        class: "text-lg font-semibold text-gray-900 hover:text-yellow-800",
        onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (emit('widgetRouteClicked')), ["prevent"]))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.title), 1)
        ]),
        _: 1
      }, 8, ["to"])
    ], 4),
    _createVNode(ReportFilterBadges, {
      type: "working_hour_curve",
      filters: _ctx.filters,
      aggregationLabel: _unref(t)(`analytics.reports.${_ctx.config.mode}_mode`),
      height: _unref(filterBadgesHeight),
      class: "ml-2"
    }, {
      default: _withCtx(() => [
        (
          _ctx.config.mode === 'end_date' &&
          _ctx.config.show_delta &&
          _ctx.filters.location.length === 0 &&
          criticalPathGroups.value &&
          criticalPathGroups.value[0] &&
          criticalPathGroups.value[0].delta
        )
          ? (_openBlock(), _createBlock(CriticalPathDelay, {
              key: 0,
              deltaForNode: criticalPathGroups.value[0].delta,
              cls: "h-min mr-2",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (isCriticalPathModalOpen.value = true))
            }, null, 8, ["deltaForNode"]))
          : _createCommentVNode("", true),
        _createVNode(_unref(MagnifyingGlassIcon), {
          class: "w-5 h-5 cursor-pointer text-gray-600 shrink-0",
          onClick: _cache[2] || (_cache[2] = ($event: any) => {
          isCriticalPathModalOpen.value = true;
          _unref(trackEvent)('progress_detail_click', { origin: 'milestone_plot' });
        })
        })
      ]),
      _: 1
    }, 8, ["filters", "aggregationLabel", "height"]),
    _createElementVNode("div", {
      style: _normalizeStyle({ height: `${_unref(chartHeight)}px` }),
      class: "overflow-hidden relative"
    }, [
      (!isLoading.value)
        ? (_openBlock(), _createBlock(ChartOrNoData, {
            key: _unref(chartKey),
            options: chartOptions.value
          }, null, 8, ["options"]))
        : _createCommentVNode("", true),
      (
          criticalPathGroups.value &&
          context.value &&
          !isChartZoomed.value &&
          _ctx.config.mode === 'sequence' &&
          _ctx.config.show_delta
        )
        ? (_openBlock(), _createBlock(MilestonePlotDeltaLabels, {
            key: 1,
            criticalPathGroups: criticalPathGroups.value,
            context: context.value,
            chartHeight: _unref(chartHeight)
          }, null, 8, ["criticalPathGroups", "context", "chartHeight"]))
        : _createCommentVNode("", true)
    ], 4),
    _createVNode(LoadingStateEmitter, { isLoading: isLoading.value }, null, 8, ["isLoading"]),
    (isCriticalPathModalOpen.value)
      ? (_openBlock(), _createBlock(CriticalPathModal, {
          key: 0,
          onClose: _cache[3] || (_cache[3] = ($event: any) => (isCriticalPathModalOpen.value = false)),
          origin: "query_value_widget"
        }))
      : _createCommentVNode("", true)
  ], 4))
}
}

})